export interface ScoreState {
  roundScores: RoundScore[];
  gameScore: number;
}

export interface RoundScore {
  wordSpellings: SpellingData[];
  score: number;
  multiplier?: number; // Only used for OneChance (final round)
}

export interface SpellingData {
  userSpelling: string;
  correctSpelling: string;
  pointValue: number | undefined;
}

export const INITIAL_STATE: ScoreState = {
  roundScores: [],
  gameScore: 0,
};

export const ScoreAction = {
  NEW_GAME: "NEW_GAME",
  WORD_SPELLED: "WORD_SPELLED",
  POINTS_EARNED: "POINTS_EARNED",
} as const;

export type ScoreAction =
  | {
      type: typeof ScoreAction.NEW_GAME;
    }
  | {
      type: typeof ScoreAction.WORD_SPELLED;
      payload: {
        roundIndex: number;
        userSpelling: string;
        correctSpelling: string;
        pointValue?: number | undefined;
        multiplier?: number | undefined;
      };
    }
  | {
      type: typeof ScoreAction.POINTS_EARNED;
      payload: { roundIndex: number; points: number };
    };

export function scoreReducer(state: ScoreState, action: ScoreAction) {
  switch (action.type) {
    case ScoreAction.NEW_GAME: {
      return {
        ...INITIAL_STATE,
      };
    }
    case ScoreAction.WORD_SPELLED: {
      const {
        roundIndex,
        userSpelling,
        correctSpelling,
        pointValue,
        multiplier,
      } = action.payload;

      // Get a copy of the score data for this round, or create a new one
      // if necessary
      const updatedRoundScores = [...state.roundScores];
      if (updatedRoundScores.length < roundIndex + 1) {
        updatedRoundScores[roundIndex] = { wordSpellings: [], score: -1 };
      }

      // Make an updated copy of the current round with the new spelling
      const currentRound = updatedRoundScores[roundIndex];
      const updatedRound: RoundScore = {
        ...currentRound,
        multiplier: multiplier ?? undefined,
        wordSpellings: [
          ...currentRound.wordSpellings,
          {
            userSpelling: userSpelling,
            correctSpelling: correctSpelling,
            pointValue: pointValue ?? undefined,
          },
        ],
      };

      updatedRoundScores[roundIndex] = updatedRound;

      return {
        ...state,
        roundScores: updatedRoundScores,
      };
    }
    case ScoreAction.POINTS_EARNED: {
      const roundIndex = action.payload.roundIndex;

      // Get a copy of the score data for this round, or create a new one
      // if necessary
      const updatedRoundScores = [...state.roundScores];
      if (updatedRoundScores.length < roundIndex + 1) {
        updatedRoundScores[roundIndex] = { wordSpellings: [], score: -1 };
      }

      // Make an updated copy of the current round with the new score
      const currentRound = updatedRoundScores[roundIndex];
      const pointsEarned = action.payload.points ?? 0;
      const updatedRound: RoundScore = {
        ...currentRound,
        score:
          currentRound.score === -1
            ? pointsEarned
            : currentRound.score + pointsEarned,
      };

      updatedRoundScores[roundIndex] = updatedRound;

      return {
        ...state,
        gameScore: state.gameScore + action.payload.points,
        roundScores: updatedRoundScores,
      };
    }
    default:
      return state;
  }
}
